import React from 'react';
import storeUtils from 'utils/Store';
import InfoButton from 'components/InfoButton/InfoButton';
import Radio from 'components/Inputs/Radio/Radio';
import StoreSelectorModalBindings from 'analytics/bindingMethods/components/globalModals/storeSelectorModal/StoreSelectorModalBindings';
import Location from 'utils/Location';
import localeUtils from 'utils/LanguageLocale';
import { modal } from 'style/config';
import {
    Divider, Text, Grid, Box, Link
} from 'components/ui';
import LanguageLocale from 'utils/LanguageLocale';
import InventoryAwareStorePickerModalEN from 'components/GlobalModals/InventoryAwareStorePickerModal/locales/InventoryAwareStorePickerModal_en_US';
import InventoryAwareStorePickerModalFR from 'components/GlobalModals/InventoryAwareStorePickerModal/locales/InventoryAwareStorePickerModal_fr_CA';
import { STORE } from 'constants/Shared';
import InventoryGrid from 'components/GlobalModals/InventoryAwareStorePickerModal/InventoryGrid';
import { VARIANTS } from 'constants/InventoryAwareStorePicker';

const { getLocalisedDataFromResource } = LanguageLocale;
const getText = getLocalisedDataFromResource(InventoryAwareStorePickerModalEN, InventoryAwareStorePickerModalFR);

const KOHLS_LINK = 'https://www.kohls.com/sale-event/sephora-at-kohls.jsp?icid=fromSephora';

function StoreListItem({
    item, index, selectedStore, showStoreDetails, selectStore, openModal, storePickupInventory, variant
}) {
    const isProductVariant = variant === VARIANTS.PRODUCT;
    const isStoreVariant = variant === VARIANTS.STORE;
    const isKohlsStore = storeUtils.isKohlsStore(item);
    const storeName = storeUtils.getStoreDisplayName(item);
    const radioId = `store_select_${item.storeId}`;
    const storeAddress = `${item.address.address1} ${item.address.city} ${item.address.state} ${item.address.postalCode}`;
    const isCurrentProductOutOfStock = storePickupInventory?.currentProduct?.outOfStock ?? true;
    const stockTextColor = isKohlsStore ? 'gray' : isCurrentProductOutOfStock ? 'red' : 'green';

    // Compute store hours
    const closingTime = storeUtils.getStoreTodayClosingTime(item.storeHours)?.replace(/^0/, '');
    const isStoreClosed = !closingTime || closingTime === STORE.CLOSED;
    const storeClosingTime = isStoreClosed ? getText('closed') : getText('openUntil', [closingTime]);

    const ariaLabelForRadioButtons = `Select ${storeName} ${storeAddress} ${storeClosingTime}`;

    // Format distance to 1 decimal place, remove .0 if whole number
    const formattedDistance = item.distance ? parseFloat(item.distance.toFixed(1)) : item.distance;

    // Radio button is disabled if store doesn't support pickup or is a Kohls store
    const isDisabled = (showStoreDetails && !(item.isRopisable || item.isBopisable)) || isKohlsStore;

    return (
        <React.Fragment>
            {index > 0 && (
                <Divider
                    marginY={4}
                    marginX={modal.outdentX}
                />
            )}
            <Box
                marginTop={index === 0 ? '1rem' : undefined}
                data-at={Sephora.debug.dataAt('change_store_found_store')}
            >
                <Radio
                    paddingY={null}
                    css={selectedStore === item || { ':hover .StoreSwitcher-name': { textDecoration: 'underline' } }}
                    dataAt={'radio_dot_store'}
                    disabled={isDisabled}
                    checked={selectedStore === item}
                    onChange={() => {
                        selectStore(item);
                    }}
                    alignItems='start'
                    id={radioId}
                    label={`Select ${storeName}`}
                    aria-label={ariaLabelForRadioButtons}
                    width='100%'
                >
                    <Grid
                        gap={2}
                        data-at={Sephora.debug.dataAt('store_data')}
                    >
                        <Grid gap={1}>
                            <Grid columns='1fr auto'>
                                <Text
                                    className='StoreSwitcher-name'
                                    fontWeight='bold'
                                    data-at={Sephora.debug.dataAt('store_name_label')}
                                >
                                    {storeName}
                                </Text>
                                <InfoButton
                                    size={16}
                                    dataAt={'store'}
                                    onClick={function (event) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        openModal({
                                            isOpen: true,
                                            currentProduct: null,
                                            selectedStore: item,
                                            useBackToStoreLink: false,
                                            showBackButton: true
                                        });
                                    }}
                                />
                            </Grid>
                            <Text color='gray'>{item.address.address1}</Text>
                            <Grid columns='1fr auto'>
                                <Text>{storeClosingTime}</Text>
                                <Text>{`${formattedDistance} ${localeUtils.isCanada() ? getText('kilometersAbbrv') : getText('milesAbbrv')}`}</Text>
                            </Grid>
                            {isProductVariant && (
                                <Text
                                    fontWeight='bold'
                                    color={stockTextColor}
                                >
                                    {isCurrentProductOutOfStock ? getText('outOfStock') : getText('inStock')}
                                </Text>
                            )}
                        </Grid>
                        {!isKohlsStore && storePickupInventory && isStoreVariant && <InventoryGrid storePickupInventory={storePickupInventory} />}
                        {isKohlsStore && (
                            <Text
                                color='gray'
                                fontSize='sm'
                                lineHeight='14px'
                            >
                                {getText('goTo')}
                                <Link
                                    color='blue'
                                    href={KOHLS_LINK}
                                    children={'kohls.com'}
                                    fontWeight='bold'
                                    onClick={event => {
                                        event.preventDefault();
                                        StoreSelectorModalBindings.triggerAnalytics(null, item.storeId);
                                        Location.setLocation(KOHLS_LINK);
                                    }}
                                />
                                {getText('kohlsCopy')}
                            </Text>
                        )}
                    </Grid>
                </Radio>
            </Box>
        </React.Fragment>
    );
}

export default StoreListItem;
